import { useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import type { DateRange } from 'react-day-picker';

import { apiRequest } from './api';
import { getSourceLabel } from './attributionDisplay';
import {
  PAGE_SIZE,
  presetToRange,
  segmentMatches,
  type ContactsListResponse,
  type DatePreset,
  type Segment,
  type SortField,
} from './contacts';
import type { Contact } from '@/types/contacts';

interface Params {
  datePreset: DatePreset;
  customRange: DateRange | undefined;
  segment: Segment;
  search: string;
  sortField: SortField;
  sortOrder: 'asc' | 'desc';
  authLoading: boolean;
}

// Data layer for the contacts list: the infinite query + the derived
// `contacts` / `filtered` / `counts`. The page owns the filter/sort state and
// passes it in; this hook owns fetching + client-side derivation.
export function useContactsQuery({
  datePreset,
  customRange,
  segment,
  search,
  sortField,
  sortOrder,
  authLoading,
}: Params) {
  // Stabilize range against re-renders. presetToRange calls `new Date()`
  // internally, so a fresh invocation per render produces a new ISO
  // `date_from` string → new queryKey → refetch → re-render → infinite loop
  // (the classic input-stability bug). Memoize on the two actual inputs.
  const range = useMemo(
    () => presetToRange(datePreset, customRange),
    [datePreset, customRange],
  );

  const query = useInfiniteQuery({
    queryKey: ['contacts', { datePreset, range, sortField, sortOrder }],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (range.date_from) params.set('date_from', range.date_from);
      if (range.date_to) params.set('date_to', range.date_to);
      params.set('sort', sortField);
      params.set('order', sortOrder);
      params.set('limit', String(PAGE_SIZE));
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest<ContactsListResponse>(`/contacts?${params.toString()}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    // Custom range requires BOTH endpoints — without this gate, the query
    // fires after the user picks just the FROM date in the calendar popover,
    // loading the table mid-selection.
    enabled:
      !authLoading &&
      (datePreset !== 'custom' ||
        (!!customRange?.from && !!customRange?.to)),
    // Keep the previous page's data visible when the queryKey changes —
    // otherwise the table goes blank during a chip switch OR when the user
    // picks only the FROM date in custom mode (queryKey shifts even though
    // enabled flips false).
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  const contacts: Contact[] = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  // Client-side filter pipeline: segment + search. Date range is server-side.
  const filtered = useMemo(() => {
    const lower = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (!segmentMatches(segment, c)) return false;
      if (lower) {
        // Locked rows arrive with identity fields stripped — guard them.
        const hay = [c.phone ?? '', c.profile_name ?? '', c.campaign ?? '']
          .join(' ')
          .toLowerCase();
        if (!hay.includes(lower)) return false;
      }
      return true;
    });
  }, [contacts, segment, search]);

  // Segment count badges over the LOADED set. Cursor pagination means counts
  // grow as the user loads more pages — honest about server-side scope.
  const counts = useMemo<Record<Segment, number>>(() => {
    const c: Record<Segment, number> = {
      all: 0,
      purchase: 0,
      qualified: 0,
      new_lead: 0,
      meta: 0,
      google: 0,
    };
    for (const row of contacts) {
      c.all += 1;
      if (row.status === 'purchase') c.purchase += 1;
      else if (row.status === 'qualified') c.qualified += 1;
      else if (row.status === 'new_lead') c.new_lead += 1;
      const label = getSourceLabel(row.source, row.medium);
      if (label === 'Meta Ads') c.meta += 1;
      if (label === 'Google Ads') c.google += 1;
    }
    return c;
  }, [contacts]);

  return { query, contacts, filtered, counts };
}
